import type { ListPartnerAttributionsOutputSchema } from "@biteasy/contracts";
import type { InfiniteData } from "@tanstack/react-query";
import { UserCheck } from "lucide-react";
import { useEffect, useRef } from "react";
import type { z } from "zod";
import { Badge } from "@/components/catalyst/Badge";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/catalyst/Table";
import { Time } from "@/components/Time";
import { formatCurrency } from "@/util/format";

interface PartnerAttributionsTableProps {
	data: InfiniteData<z.infer<typeof ListPartnerAttributionsOutputSchema>>;
	hasNextPage: boolean;
	isFetchingNextPage: boolean;
	fetchNextPage: () => void;
}

export function PartnerAttributionsTable({
	data,
	hasNextPage,
	isFetchingNextPage,
	fetchNextPage,
}: PartnerAttributionsTableProps) {
	const observerTarget = useRef<HTMLDivElement>(null);

	// Set up intersection observer for infinite scroll
	useEffect(() => {
		const observer = new IntersectionObserver(
			(entries) => {
				if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) {
					fetchNextPage();
				}
			},
			{ threshold: 0.1 },
		);

		const currentTarget = observerTarget.current;
		if (currentTarget) {
			observer.observe(currentTarget);
		}

		return () => {
			if (currentTarget) {
				observer.unobserve(currentTarget);
			}
		};
	}, [hasNextPage, isFetchingNextPage, fetchNextPage]);

	const allSubscribers = data.pages.flatMap((page) => page.items);

	if (allSubscribers.length === 0) {
		return (
			<div className="mt-8 flex flex-col items-center justify-center rounded-xl border border-dashed border-base-content/20 bg-base-100/50 px-6 py-16">
				<div className="flex size-16 items-center justify-center rounded-full bg-primary/10">
					<UserCheck className="size-8 text-primary" />
				</div>
				<h3 className="mt-4 text-lg font-semibold text-base-content">
					No attributions yet
				</h3>
				<p className="mt-2 max-w-sm text-center text-sm text-base-content/60">
					Subscribers will appear here when installations are attributed to this
					partner.
				</p>
			</div>
		);
	}

	return (
		<div>
			<Table className="mt-8">
				<TableHead>
					<TableRow>
						<TableHeader>Install ID</TableHeader>
						<TableHeader>App</TableHeader>
						<TableHeader>Method</TableHeader>
						<TableHeader>Attributed At</TableHeader>
						<TableHeader>Total Earnings</TableHeader>
					</TableRow>
				</TableHead>
				<TableBody>
					{allSubscribers.map((subscriber) => (
						<TableRow key={subscriber.installId}>
							<TableCell className="font-mono text-sm text-base-content/80">
								{subscriber.installId}
							</TableCell>
							<TableCell className="text-sm font-medium text-base-content">
								{subscriber.appName}
							</TableCell>
							<TableCell>
								<Badge
									color={
										subscriber.attributionMethod === "code" ? "sky" : "amber"
									}
								>
									{subscriber.attributionMethod === "code" ? "Code" : "Window"}
								</Badge>
							</TableCell>
							<TableCell className="text-base-content/60">
								{subscriber.attributedAt ? (
									<Time date={subscriber.attributedAt} />
								) : (
									<span className="text-base-content/40">—</span>
								)}
							</TableCell>
							<TableCell className="font-medium text-base-content">
								{formatCurrency(subscriber.totalEarnings, "usd")}
							</TableCell>
						</TableRow>
					))}
				</TableBody>
			</Table>

			{/* Intersection observer target for infinite scroll */}
			<div ref={observerTarget} className="h-4" />

			{isFetchingNextPage && (
				<div className="mt-4 text-center text-sm text-base-content/60">
					Loading more...
				</div>
			)}
		</div>
	);
}
